import { useSyncExternalStore } from 'react'
import { uk, type Dict } from './uk'
import { en } from './en'

export type Lang = 'uk' | 'en'
export type TKey = keyof Dict

const dicts: Record<Lang, Partial<Dict>> = { uk, en }
const STORAGE_KEY = 'dv.lang'

function initialLang(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'uk' || v === 'en') return v
  } catch {
    /* storage unavailable */
  }
  return 'uk'
}

let current: Lang = initialLang()
const listeners = new Set<() => void>()

export function getLang(): Lang {
  return current
}

export function setLang(l: Lang) {
  current = l
  try {
    localStorage.setItem(STORAGE_KEY, l)
  } catch {
    /* ignore */
  }
  document.documentElement.lang = l
  listeners.forEach((f) => f())
}

function subscribe(f: () => void) {
  listeners.add(f)
  return () => listeners.delete(f)
}

export type Vars = Record<string, string | number>

/** Interpolate {name} placeholders. */
export function format(s: string, vars?: Vars): string {
  if (!vars) return s
  return s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m))
}

/** Translate a key in the current language (falls back to Ukrainian, then the key). */
export function t(key: TKey, vars?: Vars): string {
  const s = dicts[current][key] ?? uk[key] ?? key
  return format(s, vars)
}

/**
 * Plural category for Ukrainian/English without ICU.
 * uk: one (1, 21…), few (2–4, 22–24…), many (0, 5–20, 25…).
 */
export function pluralCategory(n: number, lang: Lang = current): 'one' | 'few' | 'many' {
  const abs = Math.abs(n)
  if (lang === 'en') return abs === 1 ? 'one' : 'many'
  const m10 = abs % 10
  const m100 = abs % 100
  if (m10 === 1 && m100 !== 11) return 'one'
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'few'
  return 'many'
}

/** Pick a plural form: forms = "one|few|many" (English uses "one|many|many"). */
export function plural(n: number, forms: string, lang: Lang = current): string {
  const [one, few, many] = forms.split('|')
  const cat = pluralCategory(n, lang)
  const s = cat === 'one' ? one : cat === 'few' ? (few ?? many) : (many ?? few ?? one)
  return format(s ?? '', { n })
}

/** Hook: re-renders on language change and returns a bound translator. */
export function useI18n() {
  const lang = useSyncExternalStore(subscribe, getLang, getLang)
  return {
    lang,
    setLang,
    t: (key: TKey, vars?: Vars) => {
      const s = dicts[lang][key] ?? uk[key] ?? key
      return format(s, vars)
    },
    plural: (n: number, key: TKey) => plural(n, dicts[lang][key] ?? uk[key], lang),
  }
}

export function useT() {
  return useI18n().t
}
